import React, { useEffect, useState, useRef } from 'react';
import { FileBarChart, ExternalLink, Lock, X, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const SECRET_PIN = '1234';
interface CommissionReportButtonProps {
  reportUrl: string;
}
export function CommissionReportButton({
  reportUrl
}: CommissionReportButtonProps) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Auto-focus when modal opens
  useEffect(() => {
    if (open) {
      setPin('');
      setError(false);
      setSuccess(false);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);
  // Auto-submit on 4 digits
  useEffect(() => {
    if (pin.length === 4) {
      if (pin === SECRET_PIN) {
        setSuccess(true);
        // Brief success state before opening
        setTimeout(() => {
          window.open(reportUrl, '_blank', 'noopener,noreferrer');
          setOpen(false);
        }, 500);
      } else {
        setError(true);
        setTimeout(() => {
          setPin('');
          setError(false);
        }, 600);
      }
    }
  }, [pin, reportUrl]);
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(v);
  };
  return <>
      <button type="button" onClick={() => setOpen(true)} className="group bg-white rounded-lg p-4 border border-blue-100 shadow-sm hover:shadow-md hover:border-blue-300 hover:-translate-y-0.5 transition-all flex items-center justify-between gap-3 w-full text-left focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-261" data-mp="1veoozjuyx9ut">
        <div className="flex items-center gap-3 min-w-0" data-id="element-262" data-mp="1seg5byaa7nvg">
          <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-263" data-mp="1wfi4kf477zxf">
            <FileBarChart className="w-5 h-5" data-id="element-264" data-mp="265tjewpf7tj3" />
          </div>
          <div className="min-w-0" data-id="element-265" data-mp="2687e34g6v3q4">
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider leading-tight" data-id="element-266" data-mp="19gc7w13rbd9h">
              Compensation
            </p>
            <p className="text-sm font-bold text-[#0a1e5e] leading-tight mt-0.5" data-id="element-267" data-mp="1dhxp1ynbqdph">
              View Commission Report
            </p>
            <p className="text-[11px] text-gray-500 leading-tight mt-0.5 flex items-center gap-1" data-id="element-268" data-mp="1nflp943p8df1">
              <Lock className="w-3 h-3" data-id="element-269" data-mp="2tvnwnfdnjk3v" />
              PIN required · Opens in Salesforce
            </p>
          </div>
        </div>
        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 shrink-0 transition-colors" data-id="element-270" data-mp="2ytpg41ezplfv" />
      </button>

      <AnimatePresence data-id="element-271" data-mp="n6vqyn6s7fyi">
        {open && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-50" onClick={() => setOpen(false)} data-id="element-272" data-mp="i9n80kdt77w7" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.15
        }} role="dialog" aria-label="Enter PIN to view Commission Report" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-sm bg-white rounded-2xl shadow-2xl z-50 overflow-hidden" data-id="element-273" data-mp="3vmywamaf8sjo">
              <div className="bg-[#0a1e5e] text-white px-5 py-4 flex items-center justify-between gap-3" data-id="element-274" data-mp="1hzefu41vvq5h">
                <div className="flex items-center gap-2.5 min-w-0" data-id="element-275" data-mp="1mxpnc84phokj">
                  <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0" data-id="element-276" data-mp="197m7bywf6r3j">
                    <Lock className="w-5 h-5" data-id="element-277" data-mp="1dznre6ztxm44" />
                  </div>
                  <div className="min-w-0" data-id="element-278" data-mp="z9ydrcajvpmy">
                    <h3 className="text-sm font-bold leading-tight" data-id="element-279" data-mp="zb9gd8s9erch">
                      Commission Report
                    </h3>
                    <p className="text-xs text-blue-200 leading-tight mt-0.5" data-id="element-280" data-mp="146gbhs7gqnps">
                      Enter your 4-digit PIN to continue
                    </p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0 transition-colors" aria-label="Close" data-id="element-281" data-mp="bmb8ammauuvc">
                  <X className="w-4 h-4" data-id="element-282" data-mp="koaq3h5522zc" />
                </button>
              </div>

              <div className="p-6" data-id="element-283" data-mp="fmczu2ybm7ww">
                <input ref={inputRef} type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={handleInput} maxLength={4} className="sr-only" aria-label="4 digit PIN" data-id="element-284" data-mp="fq95grs2dvlr" />
                <div onClick={() => inputRef.current?.focus()} className={`flex gap-2.5 justify-center cursor-text ${error ? 'animate-shake' : ''}`} data-id="element-285" data-mp="zcfb4rrnyqla">
                  {[0, 1, 2, 3].map(i => {
                const filled = i < pin.length;
                return <div key={i} className={`w-12 h-14 rounded-lg border-2 flex items-center justify-center text-2xl font-bold transition-all ${success ? 'border-green-500 bg-green-50 text-green-600' : error ? 'border-red-400 bg-red-50 text-red-600' : filled ? 'border-[#0a1e5e] bg-blue-50 text-[#0a1e5e]' : 'border-gray-200 bg-gray-50 text-gray-300'} ${i === pin.length && !error && !success ? 'ring-2 ring-blue-300' : ''}`} data-id="element-286" data-mp="5xcp46kkxi1a">
                        {success ? <ShieldCheck className="w-6 h-6" data-id="element-287" data-mp="xrepihzmegi6" /> : filled ? '\u2022' : ''}
                      </div>;
              })}
                </div>

                {error && <p className="text-xs text-red-600 font-semibold text-center mt-3" data-id="element-288" data-mp="34h1m37o3t6v3">
                    Incorrect PIN, try again
                  </p>}
                {success && <p className="text-xs text-green-600 font-semibold text-center mt-3" data-id="element-289" data-mp="2s4h4b8v50jc0">
                    Opening Salesforce…
                  </p>}

                <p className="text-[11px] text-gray-400 mt-5 text-center" data-id="element-290" data-mp="2i9p7i79dq0ia">
                  Hint (demo): the PIN is{' '}
                  <code className="font-mono text-gray-500" data-id="element-291" data-mp="3fvd3ws5u4itt">1234</code>
                </p>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}